import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share2, Link2, CheckCircle, Loader2, MessageCircle, Heart, Sparkles } from 'lucide-react';
import { generateShareImage } from '../../utils/shareImageGenerator';
import { apiFetch } from '../../lib/api';

interface ShareModalProps {
  amount: number;
  meals: number;
  donationType: 'one-time' | 'monthly-first' | 'monthly-recurring';
  onClose: () => void;
}

export default function ShareModal({ amount, meals, donationType, onClose }: ShareModalProps) {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [generating, setGenerating] = useState(true);
  const [copied, setCopied] = useState(false);
  const [settings, setSettings] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    generateShareImage(amount, meals)
      .then((url) => {
        if (!cancelled) {
          setImageUrl(url);
          setGenerating(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGenerating(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [amount, meals]);

  useEffect(() => {
    apiFetch<Record<string, string>>('/api/public/settings')
      .then((res) => {
        if (res.success && res.data) {
          setSettings(res.data);
        }
      })
      .catch(() => {
        // silently fail, use defaults
      });
  }, []);

  const shareUrl = settings.shareUrl || window.location.origin;
  const whatsappNumber = settings.contactWhatsapp || '85262322466';
  const shareText = `我在支持 *好餸傳愛* 計劃，你也來一起改變生命！\nhttps://donate.s2c.org.hk/`;

  const whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const whatsappInquiryUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent('你好，我想查詢好餸傳愛社區飯堂的捐款事宜。')}`;
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;

  const handleCopyLink = useCallback(() => {
    const textToCopy = `我在支持 *好餸傳愛* 計劃，你也來一起改變生命！\nhttps://donate.s2c.org.hk/`;
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  const handleDownloadImage = useCallback(() => {
    if (!imageUrl) return;
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `好餸傳愛-捐款${amount}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [imageUrl, amount]);

  const displayWhatsappNumber = whatsappNumber.replace(/^852/, '');
  const formattedWhatsappNumber = displayWhatsappNumber.length >= 8
    ? `${displayWhatsappNumber.slice(-8, -4)} ${displayWhatsappNumber.slice(-4)}`
    : displayWhatsappNumber;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 flex items-center justify-between">
            <h3 className="text-white font-bold text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-300" />
              分享我的善行
            </h3>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
              aria-label="關閉"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-5">
            {/* Impact Summary */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-orange-50 text-orange-700 px-3 py-1 rounded-full text-sm font-bold mb-2">
                <Heart className="w-3.5 h-3.5 fill-orange-600 text-orange-600" />
                您的善行影響力
              </div>
              <p className="text-brand-700 font-bold text-xl mt-1">
                您的 ${amount.toLocaleString()} 將提供 {meals} 份飯餐
              </p>
              {donationType === 'monthly-first' && (
                <p className="text-brand-600 text-sm mt-1">每月持續支持，改寫更多人生</p>
              )}
            </div>

            {/* Image Preview */}
            <div className="flex justify-center">
              {generating ? (
                <div className="w-64 h-64 bg-gray-100 rounded-xl flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-brand-600 animate-spin" />
                </div>
              ) : imageUrl ? (
                <button onClick={handleDownloadImage} className="relative group">
                  <img
                    src={imageUrl}
                    alt="分享圖片預覽"
                    className="w-64 h-64 object-cover rounded-xl shadow-md group-hover:shadow-lg transition-shadow"
                  />
                  <div className="absolute inset-0 bg-black/40 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white text-sm font-medium">點擊下載圖片</span>
                  </div>
                </button>
              ) : (
                <div className="w-64 h-64 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 text-sm">
                  圖片生成失敗
                </div>
              )}
            </div>

            {/* Share Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <a
                href={whatsappShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-xl transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
              <a
                href={facebookShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-xl transition-colors"
              >
                <Share2 className="w-5 h-5" />
                Facebook
              </a>
            </div>

            {/* Copy Link */}
            <button
              onClick={handleCopyLink}
              className="w-full flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-xl transition-colors"
            >
              {copied ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-700">已複製分享訊息</span>
                </>
              ) : (
                <>
                  <Link2 className="w-5 h-5" />
                  複製分享訊息
                </>
              )}
            </button>

            {/* WhatsApp Inquiry */}
            <a
              href={whatsappInquiryUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 border-2 border-green-500 text-green-600 hover:bg-green-50 font-semibold py-3 px-4 rounded-xl transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp 查詢 ({formattedWhatsappNumber})
            </a>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
